import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { ConfirmDialog } from "@/client/components/confirm-dialog";
import { CopyButton } from "@/client/components/copy-button";
import { PageHeader } from "@/client/components/page-header";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { type ApiToken, apiTokensApi } from "@/client/features/api-tokens/api-tokens-api";
import { authApi } from "@/client/features/auth/auth-api";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";

const selectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

const EXPIRY_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "never", label: "Never" },
] as const;

const createSchema = z.object({
  name: z.string().min(1, "Name your token.").max(100, "Keep the name under 100 characters."),
  expiry: z.enum(["30", "90", "365", "never"]),
});

type CreateValues = z.infer<typeof createSchema>;

function expiryToDays(expiry: CreateValues["expiry"]): 30 | 90 | 365 | null {
  return expiry === "never" ? null : (Number(expiry) as 30 | 90 | 365);
}

function CreateTokenForm({ onCreated }: { onCreated: (token: string) => void }) {
  const [pendingValues, setPendingValues] = useState<CreateValues | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [steppingUp, setSteppingUp] = useState(false);
  // Once step-up succeeds the session is already MFA-verified, so a retry after
  // a later failure must not resend the (now consumed) TOTP.
  const [steppedUp, setSteppedUp] = useState(false);
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", expiry: "90" },
  });

  const create = async (values: CreateValues) => {
    const created = await apiTokensApi.create({
      name: values.name.trim(),
      expiresInDays: expiryToDays(values.expiry),
    });
    form.reset({ name: "", expiry: "90" });
    onCreated(created.token);
  };

  const onSubmit = async (values: CreateValues) => {
    try {
      await create(values);
    } catch (err) {
      if (err instanceof ApiError && err.code === "mfa_step_up_required") {
        setSteppedUp(false);
        setPendingValues(values);
        return;
      }
      toast.error(
        err instanceof ApiError ? err.message : "Unable to create the token. Please try again.",
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create a token</CardTitle>
        <CardDescription>
          Use an API token to call your nodes' Redfish endpoints from a terminal or automation. Send
          it as a bearer header: <code>Authorization: Bearer &lt;token&gt;</code>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 sm:max-w-md">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="CI pipeline" {...field} />
                  </FormControl>
                  <FormDescription>A label so you can tell tokens apart later.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="expiry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expires</FormLabel>
                  <FormControl>
                    <select className={selectClass} {...field}>
                      {EXPIRY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-fit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Creating…" : "Create token"}
            </Button>
          </form>
        </Form>
      </CardContent>
      <Dialog
        open={pendingValues !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingValues(null);
            setMfaCode("");
            setSteppedUp(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm with your authenticator</DialogTitle>
            <DialogDescription>
              Creating an API token requires a recent MFA verification.
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label="Authenticator code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={mfaCode}
            onChange={(event) => setMfaCode(event.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingValues(null)}>
              Cancel
            </Button>
            <Button
              disabled={steppingUp || (!steppedUp && !/^\d{6}$/.test(mfaCode))}
              onClick={async () => {
                if (!pendingValues) return;
                setSteppingUp(true);
                try {
                  if (!steppedUp) {
                    await authApi.stepUpMfa(mfaCode);
                    setSteppedUp(true);
                  }
                  await create(pendingValues);
                  setPendingValues(null);
                  setMfaCode("");
                  setSteppedUp(false);
                } catch (error) {
                  toast.error(error instanceof ApiError ? error.message : "Unable to verify MFA.");
                } finally {
                  setSteppingUp(false);
                }
              }}
            >
              {steppingUp ? "Verifying…" : "Verify and create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/** One-time reveal of a freshly created token. The secret is never retrievable again. */
function CreatedTokenDialog({ token, onClose }: { token: string | null; onClose: () => void }) {
  return (
    <Dialog open={token !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy your API token</DialogTitle>
          <DialogDescription>
            This is the only time the token is shown. Copy it now and store it somewhere safe - you
            won't be able to see it again.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted flex items-start gap-2 rounded-md p-3">
          <code className="min-w-0 flex-1 font-mono text-xs break-all">{token}</code>
          {token ? <CopyButton value={token} label="Copy token" /> : null}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeTokenDialog({ token, onRevoked }: { token: ApiToken; onRevoked: () => void }) {
  return (
    <ConfirmDialog
      trigger={
        <Button variant="outline" size="sm">
          Revoke
        </Button>
      }
      title="Revoke token?"
      description={
        <>
          "{token.name}" will stop working immediately. Anything using it will lose access. This
          can't be undone.
        </>
      }
      confirmLabel="Revoke token"
      pendingLabel="Revoking…"
      errorMessage="Unable to revoke the token. Please try again."
      onConfirm={async () => {
        await apiTokensApi.revoke(token.id);
        toast.success("Token revoked.");
        onRevoked();
      }}
    />
  );
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : "—";
}

function TokensTable({
  tokens,
  onChanged,
  readOnly,
}: {
  tokens: ApiToken[];
  onChanged: () => void;
  readOnly: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b text-left">
            <th className="py-2 pr-4 font-medium">Name</th>
            <th className="py-2 pr-4 font-medium">Token</th>
            <th className="py-2 pr-4 font-medium">Created</th>
            <th className="py-2 pr-4 font-medium">Last used</th>
            <th className="py-2 pr-4 font-medium">Expires</th>
            <th className="py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {tokens.map((t) => (
            <tr key={t.id} className="border-b last:border-0">
              <td className="py-2 pr-4 align-top">{t.name}</td>
              <td className="py-2 pr-4 align-top">
                <span className="font-mono text-xs">{t.tokenPrefix}…</span>
              </td>
              <td className="text-muted-foreground py-2 pr-4 align-top whitespace-nowrap">
                {formatDate(t.createdAt)}
              </td>
              <td className="text-muted-foreground py-2 pr-4 align-top whitespace-nowrap">
                {t.lastUsedAt ? formatDate(t.lastUsedAt) : "Never"}
              </td>
              <td className="text-muted-foreground py-2 pr-4 align-top whitespace-nowrap">
                {t.expiresAt ? formatDate(t.expiresAt) : "Never"}
              </td>
              <td className="py-2 text-right align-top">
                {readOnly ? null : <RevokeTokenDialog token={t} onRevoked={onChanged} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ApiTokensPage() {
  // Staff "view as" is read-only, and a deactivated tenant's tokens no longer
  // work (PE-991) - either way, show the tenant's tokens but hide create/revoke.
  const readOnly = useAuthStore((s) => s.impersonation != null || s.deactivated);
  const deactivated = useAuthStore((s) => s.deactivated);
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createdToken, setCreatedToken] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await apiTokensApi.list();
      setTokens(res.tokens);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load your API tokens.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="API tokens"
        description="Create tokens to authenticate automation against your nodes' Redfish endpoints."
      />

      {readOnly ? null : (
        <CreateTokenForm
          onCreated={(token) => {
            setCreatedToken(token);
            void refresh();
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Your tokens</CardTitle>
          <CardDescription>
            {deactivated
              ? "This account is deactivated, so these tokens no longer authenticate. Reactivate the account to use them again."
              : "Active tokens for your organization."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : tokens && tokens.length > 0 ? (
            <TokensTable tokens={tokens} onChanged={refresh} readOnly={readOnly} />
          ) : error ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load your API tokens. Refresh to try again.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">No API tokens yet.</p>
          )}
        </CardContent>
      </Card>

      <CreatedTokenDialog token={createdToken} onClose={() => setCreatedToken(null)} />
    </div>
  );
}
